import React, { useState, useEffect } from 'react';
import { Coins, Shield, Users, ArrowRight, ChevronDown, Check, ExternalLink, ArrowUpDown, Wallet } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import WalletMenu from '../components/WalletMenu';
import NavigationMenu from '../components/NavigationMenu';
import { web3Service } from '../lib/web3';

export default function Crypto() {
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [fromToken, setFromToken] = useState('ETH');
  const [toToken, setToToken] = useState('USDT');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState('0.5');
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  // Set ICO date to March 22, 2025
  const icoDate = new Date('2025-03-22T00:00:00Z');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = icoDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    // Check if wallet is already connected
    checkWalletConnection();

    return () => clearInterval(timer);
  }, []);

  const checkWalletConnection = async () => {
    const isConnected = web3Service.getIsConnected();
    const account = web3Service.getAccount();
    
    if (isConnected && account) {
      setWalletConnected(true);
      setWalletAddress(account);
    }
  };

  const handleConnectWallet = async () => {
    try {
      setIsConnecting(true);
      const account = await web3Service.connect('metamask');
      if (account) {
        setWalletConnected(true);
        setWalletAddress(account);
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectWallet = async () => {
    try {
      await web3Service.disconnect();
      setWalletConnected(false);
      setWalletAddress('');
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);
    }
  };

  const handleJoinWhitelist = () => {
    window.location.href = 'mailto:info@privatecharterx.com?subject=PVCX Token Whitelist Request';
  };

  const handleBuyOnUniswap = () => {
    window.open('https://app.uniswap.org', '_blank');
  };

  const handleSwapTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
  };

  const calculateToAmount = (value: string) => {
    setFromAmount(value);
    
    // Mock exchange rate calculation
    const rates: Record<string, Record<string, number>> = {
      'ETH': { 'USDT': 3450, 'BTC': 0.055, 'PVCX': 2300 },
      'BTC': { 'USDT': 62000, 'ETH': 18.2, 'PVCX': 41000 },
      'USDT': { 'ETH': 0.00029, 'BTC': 0.000016, 'PVCX': 0.67 },
      'PVCX': { 'ETH': 0.00043, 'BTC': 0.000024, 'USDT': 1.5 }
    };
    
    if (value && rates[fromToken] && rates[fromToken][toToken]) {
      const rate = rates[fromToken][toToken];
      const result = parseFloat(value) * rate;
      setToAmount(result.toFixed(6));
    } else {
      setToAmount('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-gray-900 font-medium mb-6">
              <Coins size={18} />
              <span>Crypto Exchange</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Swap Cryptocurrencies</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Easily swap between cryptocurrencies with our integrated exchange powered by Uniswap. Connect your wallet to get started.
            </p>
          </div>

          {/* Swap Interface */}
          <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-16">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Swap Tokens</h2>
                {walletConnected ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-mono">{walletAddress.substring(0, 6)}...{walletAddress.substring(walletAddress.length - 4)}</span>
                  </div>
                ) : (
                  <button
                    onClick={handleConnectWallet}
                    disabled={isConnecting}
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    {isConnecting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <Wallet size={16} />
                        <span>Connect Wallet</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* From Token */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  From
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      value={fromAmount}
                      onChange={(e) => calculateToAmount(e.target.value)}
                      placeholder="0.0"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-lg"
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={fromToken}
                      onChange={(e) => {
                        setFromToken(e.target.value);
                        calculateToAmount(fromAmount);
                      }}
                      className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 pr-10 font-medium focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="ETH">ETH</option>
                      <option value="BTC">BTC</option>
                      <option value="USDT">USDT</option>
                      <option value="PVCX">PVCX</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              {/* Swap Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleSwapTokens}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <ArrowUpDown size={20} className="text-gray-700" />
                </button>
              </div>
              
              {/* To Token */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  To
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={toAmount}
                      readOnly
                      placeholder="0.0"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-lg"
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={toToken}
                      onChange={(e) => {
                        setToToken(e.target.value);
                        calculateToAmount(fromAmount);
                      }}
                      className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 pr-10 font-medium focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="USDT">USDT</option>
                      <option value="ETH">ETH</option>
                      <option value="BTC">BTC</option>
                      <option value="PVCX">PVCX</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              {/* Slippage Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slippage Tolerance
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSlippage('0.1')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      slippage === '0.1' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    0.1%
                  </button>
                  <button
                    onClick={() => setSlippage('0.5')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      slippage === '0.5' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    0.5%
                  </button>
                  <button
                    onClick={() => setSlippage('1.0')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      slippage === '1.0' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    1.0%
                  </button>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      value={slippage}
                      onChange={(e) => setSlippage(e.target.value)}
                      className="w-full px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</div>
                  </div>
                </div>
              </div>
              
              {/* Swap Button */}
              <button
                onClick={handleBuyOnUniswap}
                disabled={!walletConnected || !fromAmount}
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {!walletConnected ? (
                  <>
                    <Wallet size={18} />
                    <span>Connect Wallet to Swap</span>
                  </>
                ) : !fromAmount ? (
                  <span>Enter an amount</span>
                ) : (
                  <>
                    <ArrowUpDown size={18} />
                    <span>Swap {fromToken} to {toToken}</span>
                  </>
                )}
              </button>

              {walletConnected && (
                <button
                  onClick={handleDisconnectWallet}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Disconnect wallet
                </button>
              )}
              
              <div className="text-xs text-gray-500 text-center">
                Powered by Uniswap Protocol • Minimum received: {fromAmount && toAmount ? (parseFloat(toAmount) * (1 - parseFloat(slippage) / 100)).toFixed(6) : '0'} {toToken}
              </div>
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl p-8 md:p-12 mb-16 text-gray-900 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">PVCX Token Presale Starts In</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="bg-white shadow-sm rounded-xl p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">{timeLeft.days}</div>
                <div className="text-gray-500">Days</div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">{timeLeft.hours}</div>
                <div className="text-gray-500">Hours</div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">{timeLeft.minutes}</div>
                <div className="text-gray-500">Minutes</div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">{timeLeft.seconds}</div>
                <div className="text-gray-500">Seconds</div>
              </div>
            </div>
          </div>

          {/* Token Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div>
              <h2 className="text-3xl font-bold mb-6">Token Details</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Token Name</span>
                    <span className="font-medium">PrivateCharterX Token</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Token Symbol</span>
                    <span className="font-medium">PVCX</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Supply</span>
                    <span className="font-medium">100,000,000 PVCX</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booking Fee to Token</span>
                    <span className="font-medium">5% of each booking</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Network</span>
                    <span className="font-medium">Ethereum</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-6">Token Distribution</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Presale</div>
                      <div className="text-sm text-gray-600">Early supporters and strategic partners</div>
                    </div>
                    <span className="font-medium">30%</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Reward Pool</div>
                      <div className="text-sm text-gray-600">Flight rewards and loyalty program</div>
                    </div>
                    <span className="font-medium">25%</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Team & Advisors</div>
                      <div className="text-sm text-gray-600">12-month vesting period</div>
                    </div>
                    <span className="font-medium">15%</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Marketing & Partnerships</div>
                      <div className="text-sm text-gray-600">Growth and expansion</div>
                    </div>
                    <span className="font-medium">15%</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Liquidity Pool</div>
                      <div className="text-sm text-gray-600">Exchange listings and trading</div>
                    </div>
                    <span className="font-medium">10%</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Reserve</div>
                      <div className="text-sm text-gray-600">Future development and contingency</div>
                    </div>
                    <span className="font-medium">5%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Token Utility */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Token Utility & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Flight Rewards</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">5% of each booking goes to token liquidity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Use tokens to book flights and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Earn bonus tokens for frequent bookings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Access to exclusive empty leg deals</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Premium Benefits</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Priority booking and flight changes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Access to VIP lounges worldwide</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Complimentary upgrades when available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Exclusive event invitations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Whitelist CTA */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 p-10 rounded-2xl text-center">
            <h2 className="text-3xl font-bold mb-4">Join the PVCX Token Whitelist</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Be among the first to access our revolutionary token when it launches. Early supporters receive exclusive benefits and bonus tokens.
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={handleJoinWhitelist}
                  className="flex-1 bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  <span>Join Whitelist</span>
                  <ArrowRight size={18} />
                </button>
                <button 
                  onClick={handleBuyOnUniswap}
                  className="flex-1 bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <span>Buy on Uniswap</span>
                  <ExternalLink size={18} />
                </button>
              </div>
              <p className="text-sm mt-4 text-gray-600">
                Email us at info@privatecharterx.com to join the whitelist and receive updates about the PVCX token launch.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}